import classNames from 'classnames';
import { Link, useLocation } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { Badge, PageHeader } from '@/components/ui/page';
import { SegmentChip } from '@/components/ui/segment-chip';
import { SortTh } from '@/components/ui/sort-th';
import { btn, table } from '@/components/ui/styles';
import { formatDate } from '@/utils/format';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import type { Segment, SegmentList } from './api-types';
import { KIND_LABEL, describeRule, kindsOf } from './rules';

/** What the performance rules count on, by the API's metrics source. */
const METRICS_NOTE: Record<string, string> = {
  stats: 'Performance rules run on the last 30 days from the platform’s stats service.',
  mock: 'Performance rules run on made-up numbers: the local mock metrics source.',
  none: 'Performance rules are not counted: there is no metrics source.',
};

const COLUMNS: SortColumns<Segment> = {
  name: { value: (s) => s.name },
  casinos: { value: (s) => s.casinos, desc: true },
  leads: { value: (s) => s.leads, desc: true },
  updated: { value: (s) => dateValue(s.updated_at), desc: true },
};

/**
 * Segments (SEG-1…3): groups of casinos and leads by performance, geography and
 * type, which a message targets (SEG-3). The counts are what the rules pick right
 * now; performance rules run on the metrics source (the stats service; mock locally).
 */
const SegmentsPage = () => {
  const deleted = (useLocation().state as { deleted?: string } | null)?.deleted;
  const list = useApi<SegmentList>('/segments');
  // Each country's own segment (SEG-2) is not listed here: it shows wherever segments are
  // filtered or picked, under Countries.
  const { rows, sort, toggle } = useSort((list.data?.items ?? []).filter((s) => !s.country), COLUMNS);

  return (
    <div>
      <PageHeader
        title="Segments"
        description="Groups of casinos and leads by performance, geography and type. A message targets one or more of them. Each country also has a segment of its own, made by Prisma, that you pick under Countries in the segment filters."
        actions={
          <Link to="/segments/new" className={btn.primary}>
            New segment
          </Link>
        }
      />
      {deleted && (
        <p role="status" className="mb-4 text-sm text-fg-muted">
          Deleted “{deleted}”.
        </p>
      )}

      {list.error && !list.data ? (
        <StateMessage title="Segments could not be loaded" tone="error">
          {list.error}
        </StateMessage>
      ) : !list.data ? (
        <div className="h-40 animate-pulse rounded-card bg-surface shadow-e1" />
      ) : rows.length === 0 ? (
        <StateMessage title="No segments yet">A segment picks casinos and leads by rules, so a message can target them.</StateMessage>
      ) : (
        <div className={table.wrap}>
          <div className="overflow-x-auto">
            <table className={classNames(table.table, 'md:min-w-[860px]')}>
              <thead className={classNames(table.head, 'hidden md:table-header-group')}>
                <tr>
                  <SortTh label="Segment" column="name" sort={sort} onSort={toggle} />
                  <th scope="col" className={table.th}>Kind</th>
                  <th scope="col" className={table.th}>Rules</th>
                  <SortTh label="Casinos" column="casinos" sort={sort} onSort={toggle} right />
                  <SortTh label="Leads" column="leads" sort={sort} onSort={toggle} right />
                  <SortTh label="Updated" column="updated" sort={sort} onSort={toggle} />
                </tr>
              </thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} className={classNames(table.row, 'align-top')}>
                    <td className={table.td}>
                      <Link
                        to={`/segments/${s.id}`}
                        className="flex min-h-11 max-w-full items-center transition-opacity hover:opacity-80 md:inline-flex md:min-h-0"
                      >
                        <SegmentChip name={s.name} color={s.color} />
                      </Link>
                      {s.description && <p className="max-w-xs text-xs text-fg-muted md:mt-0.5">{s.description}</p>}
                      {/* On a phone the other columns fold in here. */}
                      <div className="mt-1.5 space-y-1 md:hidden">
                        <p className="text-xs tabular-nums text-fg">
                          {s.casinos} {s.casinos === 1 ? 'casino' : 'casinos'} · {s.leads} {s.leads === 1 ? 'lead' : 'leads'}
                        </p>
                        <p className="line-clamp-2 text-xs text-fg-muted">{s.rules.map(describeRule).join(' · ') || 'No rules: everyone'}</p>
                        {s.skipped > 0 && (
                          <p className="text-xs text-fg-faint">
                            {s.skipped === 1 ? 'One rule is' : `${s.skipped} rules are`} not counted yet: no data for it.
                          </p>
                        )}
                      </div>
                    </td>
                    <td className={classNames(table.td, 'hidden md:table-cell')}>
                      <span className="flex flex-wrap gap-1">
                        {kindsOf(s.rules).map((k) => (
                          <Badge key={k}>{KIND_LABEL[k]}</Badge>
                        ))}
                        {s.rules.length === 0 && <span className="text-fg-faint">Everyone</span>}
                      </span>
                    </td>
                    <td className={classNames(table.td, 'hidden max-w-xs text-fg-muted md:table-cell')}>
                      {s.rules.map(describeRule).join(' · ') || '—'}
                      {s.skipped > 0 && (
                        <p className="mt-0.5 text-xs text-fg-faint">
                          {s.skipped === 1 ? 'One rule is' : `${s.skipped} rules are`} not counted yet: no data for it.
                        </p>
                      )}
                    </td>
                    <td className={classNames(table.td, 'hidden text-right tabular-nums text-fg md:table-cell')}>{s.casinos}</td>
                    <td className={classNames(table.td, 'hidden text-right tabular-nums text-fg md:table-cell')}>{s.leads}</td>
                    <td className={classNames(table.td, 'hidden whitespace-nowrap md:table-cell')}>
                      <p className="tabular-nums text-fg-muted">{formatDate(s.updated_at)}</p>
                      {s.updated_by && <p className="text-xs text-fg-faint">{s.updated_by}</p>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-line px-4 py-3 text-xs text-fg-faint">
            {list.data.metrics_source === 'mock' && (
              <DemoPill title="GGR, players and velocity come from the local mock metrics source.">
                Demo metrics
              </DemoPill>
            )}
            <span>{METRICS_NOTE[list.data.metrics_source] ?? METRICS_NOTE.stats}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default SegmentsPage;
